import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Copy, MessageCircle, CheckCircle2, IndianRupee, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * UpiPayCard — reusable inline UPI payment widget.
 * Shows: heylpmate@upi + PhonePe QR + Pay Now deep link + "I've Paid — send screenshot on WhatsApp"
 * Props:
 *  - amount   (number, required)
 *  - purpose  (string) e.g. "wallet_topup" / "booking_1234" / "flatmate_credits"
 *  - referenceId (string, optional)
 *  - onLogged (fn(txn)) called after user records "I've Paid"
 */
export default function UpiPayCard({ amount, purpose = "generic", referenceId = null, onLogged }) {
  const { token } = useAuth();
  const [cfg, setCfg] = useState({ vpa: "heylpmate@upi", name: "HeylpMate", qr_url: "", whatsapp: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => {
      const upi = r.data?.upi || {};
      setCfg({
        vpa: upi.vpa || "9311788748-2@axl",
        name: upi.name || "HeylpMate",
        qr_url: upi.qr_url && !upi.qr_url.startsWith("/") ? upi.qr_url : "",
        whatsapp: r.data?.whatsapp_number || "",
      });
    }).catch(() => {});
  }, []);

  const upiLink = useMemo(() => {
    const params = new URLSearchParams({
      pa: cfg.vpa || "9311788748-2@axl",
      pn: cfg.name || "HeylpMate",
      am: String(amount || 0),
      cu: "INR",
      tn: `${purpose}${referenceId ? ` #${referenceId}` : ""}`.slice(0, 80),
    });
    return `upi://pay?${params.toString()}`;
  }, [cfg, amount, purpose, referenceId]);

  // Client-side generated QR code (SVG data URI via api.qrserver.com fallback)
  const qrImg = useMemo(() => {
    if (cfg.qr_url) return cfg.qr_url;
    return `https://api.qrserver.com/v1/create-qr-code/?size=380x380&margin=8&color=00604A&bgcolor=FFFFFF&data=${encodeURIComponent(upiLink)}`;
  }, [cfg.qr_url, upiLink]);

  const copyVpa = async () => {
    try {
      await navigator.clipboard.writeText(cfg.vpa);
      toast.success(`UPI id copied — ${cfg.vpa}`);
    } catch { /* noop */ }
  };

  const logPaid = async () => {
    if (!token) {
      toast.error("Please sign up / log in first");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/upi/txn`,
        { amount_inr: Number(amount), purpose, reference_id: referenceId, payer_note: note || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone(true);
      onLogged?.(r.data?.txn);
      toast.success("Payment recorded — awaiting admin confirmation");
      // Open WhatsApp with prefilled message
      if (cfg.whatsapp) {
        const msg = encodeURIComponent(
          `Hi HeylpMate, I've paid ₹${amount} for ${purpose}${referenceId ? ` (#${referenceId})` : ""}. Sending screenshot now.`
        );
        const num = cfg.whatsapp.replace(/[^\d]/g, "");
        window.open(`https://wa.me/${num}?text=${msg}`, "_blank");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="upi-pay-card"
         className="rounded-3xl border p-5 md:p-7 bg-white shadow-sm"
         style={{ borderColor: "#E4E7E5" }}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Pay via UPI</div>
          <div className="mt-1 font-display text-2xl md:text-3xl flex items-center gap-1" style={{ color: "#00604A" }}>
            <IndianRupee className="w-6 h-6" />{amount}
          </div>
          <div className="text-xs mt-1" style={{ color: "#667085" }}>Purpose: <b>{purpose}</b>{referenceId ? ` · Ref ${referenceId}` : ""}</div>
        </div>

        {/* QR — Emergent-style scan-to-pay */}
        <div className="w-32 h-32 rounded-2xl overflow-hidden border shrink-0 bg-white" style={{ borderColor: "#00604A" }}>
          <img data-testid="upi-qr-img" src={qrImg} alt="HeylpMate UPI QR"
               className="w-full h-full object-contain p-1" />
        </div>
      </div>

      {/* VPA copy strip */}
      <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border px-4 py-3"
           style={{ borderColor: "#E4E7E5", background: "#F7FBF9" }}>
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "#667085" }}>UPI id</div>
          <div className="font-mono font-bold text-base" data-testid="upi-vpa" style={{ color: "#00604A" }}>{cfg.vpa}</div>
        </div>
        <button data-testid="upi-copy-btn" onClick={copyVpa}
                className="px-3 py-2 rounded-full text-xs font-bold inline-flex items-center gap-1.5 hover:opacity-90"
                style={{ background: "#00604A", color: "#fff" }}>
          <Copy className="w-3.5 h-3.5" /> Copy
        </button>
      </div>

      {/* Actions */}
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <a href={upiLink} data-testid="upi-pay-now-btn"
           className="py-3 rounded-full font-bold text-white shadow-md hover:opacity-95 inline-flex items-center justify-center gap-2"
           style={{ background: "#F45118" }}>
          Pay Now <ExternalLink className="w-4 h-4" />
        </a>
        <button data-testid="upi-i-paid-btn" onClick={logPaid} disabled={loading || done}
           className="py-3 rounded-full font-bold inline-flex items-center justify-center gap-2 border-2"
           style={{ borderColor: "#00604A", color: done ? "#fff" : "#00604A", background: done ? "#00604A" : "#fff" }}>
          {done ? (<><CheckCircle2 className="w-4 h-4" /> Recorded</>) :
                  (<><MessageCircle className="w-4 h-4" /> I've Paid — WhatsApp screenshot</>)}
        </button>
      </div>

      {/* Optional note */}
      {!done && (
        <input data-testid="upi-note-input"
               value={note} onChange={(e) => setNote(e.target.value)}
               placeholder="Add a payer note (optional — e.g. paid via GPay)"
               className="mt-3 w-full text-sm rounded-2xl border-2 px-4 py-2.5 outline-none"
               style={{ borderColor: "#E4E7E5" }} />
      )}

      <p className="mt-3 text-[11px]" style={{ color: "#667085" }}>
        Scan the PhonePe QR or tap <b>Pay Now</b>. After paying, tap <b>I've Paid</b> — we'll open WhatsApp so you can send the screenshot for instant admin confirmation.
      </p>
    </div>
  );
}
